/**
 * Salevo chat widget loader.
 *
 * Reads `window.SalevoChatConfig`, injects a floating launcher and an
 * iframe that points at the `/embed/chat` route of this same SPA, then
 * exchanges runtime signals with the iframe via `postMessage`.
 *
 * Defaults for `appUrl`/`apiUrl` are derived from the `<script>` tag that
 * loaded this file, so the customer only needs to provide `widgetKey`.
 */

import { collapseLauncherToAvatar, normalizeAppearance } from './appearance';
import { hasWidgetConfiguration, normalizeBranding } from './branding';
import type { BrandingPayload } from './branding';
import { buildAppearanceLauncher } from './launcher';

export {};

interface SalevoChatConfig {
  widgetKey: string;
  apiUrl?: string;
  appUrl?: string;
  position?: 'left' | 'right';
  primaryColor?: string;
  zIndex?: number;
}

interface SalevoChatPublicApi {
  open: () => void;
  close: () => void;
  toggle: () => void;
  destroy: () => void;
}

declare global {
  interface Window {
    SalevoChatConfig?: SalevoChatConfig;
    SalevoChat?: SalevoChatPublicApi;
  }
}

const CONTAINER_ID = 'salevo-chat-widget-root';
const EMBED_PATH = '/embed/chat';
const PANEL_WIDTH_DEFAULT = '384px';
const PANEL_HEIGHT_DEFAULT = '572px';
const PANEL_WIDTH_COMPACT = '315px';
const PANEL_HEIGHT_COMPACT = '456px';
const PANEL_MAX_HEIGHT = 'calc(100vh - 24px)';


/** Fullscreen on small screens (narrow or short), any orientation. */
const MQ_MOBILE = '(max-width: 768px), (max-height: 500px)';
const MQ_COMPACT = '(max-width: 900px), (max-height: 720px)';

const currentScript =
  typeof document !== 'undefined'
    ? (document.currentScript as HTMLScriptElement | null)
    : null;

declare const process: { env: { FALLBACK_APP_URL: string; FALLBACK_API_URL: string } };
const FALLBACK_APP_URL = process.env.FALLBACK_APP_URL;
const FALLBACK_API_URL = process.env.FALLBACK_API_URL;

function originFromScript(): string | null {
  try {
    if (!currentScript?.src) return null;
    return new URL(currentScript.src).origin;
  } catch {
    return null;
  }
}

function isLocalhostOrigin(origin: string): boolean {
  try {
    const { hostname } = new URL(origin);
    return hostname === 'localhost' || hostname === '127.0.0.1';
  } catch {
    return false;
  }
}

function resolveDefaultApiUrl(scriptOrigin: string | null): string {
  if (!scriptOrigin) {
    return FALLBACK_API_URL;
  }
  const apiFb = FALLBACK_API_URL;
  if (isLocalhostOrigin(scriptOrigin) && isLocalhostOrigin(apiFb)) {
    return scriptOrigin;
  }
  try {
    if (
      apiFb.startsWith('https://') &&
      new URL(apiFb).origin !== new URL(scriptOrigin).origin
    ) {
      return apiFb;
    }
  } catch {
    // ignore invalid FALLBACK_API_URL
  }
  return scriptOrigin;
}

function main(): void {
  const config = window.SalevoChatConfig;

  if (!config || !config.widgetKey) {
    console.error('[SalevoChat] window.SalevoChatConfig.widgetKey não informado.');
    return;
  }

  if (document.getElementById(CONTAINER_ID)) {
    return;
  }

  const scriptOrigin = originFromScript();
  const defaultAppUrl = scriptOrigin || FALLBACK_APP_URL;
  const defaultApiUrl = resolveDefaultApiUrl(scriptOrigin);

  /**
   * Palpite síncrono, substituído pela configuração salva quando ela chega.
   *
   * O snippet do cliente carrega a posição do dia em que ele o copiou. Usá-la
   * aqui evita o launcher nascer num canto e pular para o outro; usá-la como
   * ÚNICA fonte — que era o caso — significava que trocar o canto no painel
   * nunca chegava ao site.
   */
  let position: 'left' | 'right' = config.position === 'left' ? 'left' : 'right';
  const primaryColor = config.primaryColor || '#00D4C8';
  const zIndex = config.zIndex ?? 2147483000;
  const apiUrl = (config.apiUrl || defaultApiUrl).replace(/\/$/, '');
  const appBase = (config.appUrl || defaultAppUrl).replace(/\/$/, '');
  const appUrl = appBase.endsWith(EMBED_PATH) ? appBase : `${appBase}${EMBED_PATH}`;

  const container = document.createElement('div');
  container.id = CONTAINER_ID;
  container.style.all = 'initial';

  let branding: BrandingPayload = {};
  let launcherRevealed = false;
  let widgetConfigured = false;

  /**
   * O launcher é construído duas vezes, e isso é deliberado.
   *
   * A primeira, aqui, usa os defaults e a cor do `SalevoChatConfig` — que estão
   * disponíveis de forma síncrona. Ele nasce escondido. A segunda acontece
   * quando a branding chega com a `appearance` real, e substitui esta.
   *
   * A alternativa seria não construir nada até a branding chegar, mas então o
   * `panel` e o `container` ficariam sem o irmão que define a ordem de
   * empilhamento — e o layout responsivo não teria em que se apoiar.
   */
  let launcher: HTMLElement = buildAppearanceLauncher({
    appearance: normalizeAppearance(null),
    branding: { primaryColor },
    position,
    zIndex,
    onOpen: () => setOpen(true),
  });
  launcher.style.visibility = 'hidden';
  launcher.style.display = 'none';

  const panel = buildPanel(position, zIndex);
  const relayoutPanel = setupResponsiveLayout(panel, () => position);
  const iframe = buildIframe(appUrl, {
    widgetKey: config.widgetKey,
    apiUrl,
    primaryColor,
    parentOrigin: window.location.origin,
  });

  panel.appendChild(iframe);
  container.appendChild(launcher);
  container.appendChild(panel);
  document.body.appendChild(container);

  let isOpen = false;
  // O "x" dos launchers Barra e Destacado não esconde o atendimento por toda a
  // sessão: ele o recolhe para o avatar. Ao abrir pelo avatar, a próxima
  // exibição volta ao modelo configurado.
  let launcherCollapsed = false;

  /**
   * Manda a pergunta de acesso rápido para o app dentro do iframe.
   *
   * `targetOrigin` explícito e não `'*'`: a mensagem carrega texto que o
   * visitante vai ver como se ele tivesse escrito, e o iframe é de outra
   * origem. Postar para `'*'` entregaria isso a qualquer documento que
   * estivesse ali.
   *
   * O app pode ainda não estar pronto quando o visitante clica; por isso a
   * pergunta é reenviada no `ready` que ele emite.
   */
  let pendingQuickQuestion: string | null = null;

  const sendQuickQuestion = (question: string): void => {
    const target = iframe.contentWindow;
    if (!target) return;
    target.postMessage(
      { source: 'salevo-chat-loader', type: 'quick-question', question },
      new URL(appUrl).origin
    );
  };

  const maybeRevealFromBranding = (payload: BrandingPayload): void => {
    branding = {
      ...branding,
      ...payload,
      launcherTextColor: payload.launcherTextColor ?? branding.launcherTextColor ?? 'black',
      prechatFields: payload.prechatFields ?? branding.prechatFields ?? null,
      // As duas vias (fetch e postMessage) chegam aqui; a que vier sem posição
      // não apaga a que veio com.
      position: payload.position ?? branding.position ?? null,
    };

    // Configuração ausente não é um convite para desenhar o launcher padrão.
    // Assim um snippet instalado antes de o widget ser configurado fica invisível
    // para o visitante, como esperado.
    if (!hasWidgetConfiguration(branding)) return;

    /**
     * A posição salva ganha do palpite do snippet.
     *
     * O painel é reposicionado por `relayoutPanel`, que passa por
     * `applyPanelLayout` e zera a borda oposta — sem isso o painel ficaria com
     * `left` e `right` ao mesmo tempo e esticaria de ponta a ponta. O launcher
     * não precisa de tratamento: o `renderLauncher` logo abaixo o reconstrói
     * inteiro, e é ele quem aplica o lado.
     */
    if (payload.position && payload.position !== position) {
      position = payload.position;
      relayoutPanel();
    }

    /**
     * Reconstrói o launcher com a aparência real e o revela.
     *
     * Um caminho só: o launcher legado não existe mais, e o widget sem aparência
     * salva já saiu acima, por `hasWidgetConfiguration`, sem revelar nada.
     *
     * Reconstruir em vez de mutar é o que dispensa os antigos
     * `applyLauncherBranding`/`updateLauncherAvatar`: a aparência chega com os
     * valores finais, e mudar de modelo (de `circular` para `barra`, digamos)
     * significa outra árvore de DOM, não outros atributos.
     */
    renderLauncher(branding.appearance);
    widgetConfigured = true;
    launcherRevealed = true;
    launcher.style.visibility = 'visible';
    launcher.style.display = isOpen ? 'none' : 'flex';
  };

  void fetchWidgetBranding(apiUrl, config.widgetKey, window.location.origin).then((fetched) => {
    if (launcherRevealed || !fetched) return;
    maybeRevealFromBranding(fetched);
  });

  const setOpen = (next: boolean) => {
    if (!widgetConfigured) return;
    if (next && launcherCollapsed) {
      launcherCollapsed = false;
      renderLauncher(branding.appearance);
    }
    isOpen = next;
    panel.style.display = next ? 'block' : 'none';
    if (next) {
      launcher.style.display = 'none';
    } else {
      launcher.style.display = launcherRevealed ? 'flex' : 'none';
    }
    // No launcher da aparência a raiz é um container: o `aria-expanded` pertence
    // ao gatilho, que é o elemento com papel de botão.
    const expandable =
      launcher.matches('button') ? launcher : launcher.querySelector('[data-salevo-launcher-trigger]');
    expandable?.setAttribute('aria-expanded', next ? 'true' : 'false');
  };

  /**
   * (Re)constrói o launcher a partir da aparência.
   *
   * Substitui o nó NO LUGAR para não mexer na ordem dos filhos do container: o
   * painel é irmão e depende dela para o empilhamento.
   */
  const renderLauncher = (raw: Record<string, unknown> | null | undefined): void => {
    const configuredAppearance = normalizeAppearance(raw, {
      launcherMessage: branding.launcherMessage,
      launcherTextColor: branding.launcherTextColor,
    });
    const appearance = launcherCollapsed
      ? collapseLauncherToAvatar(configuredAppearance)
      : configuredAppearance;

    if (!appearance.showLauncher) {
      // Configurado para não exibir: some com o botão e deixa apenas a API
      // programática (`window.SalevoChat.open()`) como forma de abrir.
      launcher.remove();
      launcher = document.createElement('div');
      launcher.style.display = 'none';
      container.insertBefore(launcher, panel);
      return;
    }

    const novo = buildAppearanceLauncher({
      appearance,
      branding: {
        primaryColor: branding.primaryColor ?? primaryColor,
        chatbotAvatarUrl: branding.chatbotAvatarUrl ?? null,
        chatbotName: branding.chatbotName ?? null,
      },
      position,
      zIndex,
      onOpen: () => setOpen(true),
      onClose: () => {
        launcherCollapsed = true;
        renderLauncher(branding.appearance);
        launcher.style.visibility = 'visible';
        launcher.style.display = 'flex';
      },
      onQuickQuestion: (question) => {
        pendingQuickQuestion = question;
        setOpen(true);
        sendQuickQuestion(question);
      },
    });

    container.replaceChild(novo, launcher);
    launcher = novo;
    // Quem revela é o chamador: aqui só se troca o elemento.
  };

  window.addEventListener('message', (event) => {
    if (event.source !== iframe.contentWindow) return;
    if (!event.data || typeof event.data !== 'object') return;
    const data = event.data as {
      source?: string;
      type?: string;
      branding?: BrandingPayload;
    };
    if (data.source !== 'salevo-chat-app') return;

    switch (data.type) {
      case 'close':
        setOpen(false);
        break;
      case 'branding':
        if (data.branding) {
          maybeRevealFromBranding(data.branding);
        }
        break;
      case 'ready':
        /**
         * O visitante pode clicar numa pergunta de acesso rápido antes de o app
         * dentro do iframe terminar de montar — é o caso comum, porque o clique
         * é o que abre o painel. A pergunta enviada naquele instante se perde,
         * então ela é reenviada aqui, quando o app avisa que está pronto.
         */
        if (pendingQuickQuestion) {
          sendQuickQuestion(pendingQuickQuestion);
          pendingQuickQuestion = null;
        }
        break;
      case 'resize':
        break;
      default:
        break;
    }
  });

  window.SalevoChat = {
    open: () => setOpen(true),
    close: () => setOpen(false),
    toggle: () => setOpen(!isOpen),
    destroy: () => {
      container.remove();
      delete window.SalevoChat;
    },
  };
}

async function fetchWidgetBranding(
  apiUrl: string,
  widgetKey: string,
  parentOrigin: string
): Promise<BrandingPayload | null> {
  try {
    const params = new URLSearchParams({ widgetKey, parentOrigin });
    const res = await fetch(`${apiUrl}/api/public/widget/config?${params.toString()}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      credentials: 'omit',
      mode: 'cors',
    });
    if (!res.ok) return null;
    const body = (await res.json()) as { data?: { branding?: Record<string, unknown> } };
    const raw = body?.data?.branding;
    if (!raw || typeof raw !== 'object') return null;
    return normalizeBranding(raw);
  } catch {
    return null;
  }
}

function applyPanelLayout(
  panel: HTMLDivElement,
  position: 'left' | 'right',
  mobile: boolean,
  compact: boolean
): void {
  if (mobile) {
    // Stretch via insets so portrait fills the visual viewport reliably
    // (percentage height on position:fixed is flaky on mobile browsers).
    panel.style.top = '0';
    panel.style.left = '0';
    panel.style.right = '0';
    panel.style.bottom = '0';
    panel.style.width = 'auto';
    panel.style.height = 'auto';
    panel.style.maxHeight = 'none';
    panel.style.borderRadius = '0';
    panel.style.boxShadow = 'none';
    return;
  }

  const opposite: 'left' | 'right' = position === 'left' ? 'right' : 'left';
  panel.style.top = 'auto';
  panel.style[opposite] = 'auto';
  panel.style.width = compact ? PANEL_WIDTH_COMPACT : PANEL_WIDTH_DEFAULT;
  panel.style.height = compact ? PANEL_HEIGHT_COMPACT : PANEL_HEIGHT_DEFAULT;
  panel.style.maxHeight = PANEL_MAX_HEIGHT;
  panel.style.bottom = 'calc(20px + env(safe-area-inset-bottom, 0px))';
  panel.style[position] = position === 'left'
    ? 'calc(20px + env(safe-area-inset-left, 0px))'
    : 'calc(20px + env(safe-area-inset-right, 0px))';
  panel.style.borderRadius = '24px';
  panel.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.12)';
}

/**
 * Layout responsivo do painel.
 *
 * Deixou de receber o launcher: o dimensionamento dele era `applyLauncherSize`,
 * que procurava `.salevo-launcher-circle` do launcher legado. Com a aparência
 * ligada por padrão esse elemento não existe mais, e o launcher novo se
 * dimensiona pelo próprio CSS inline.
 *
 * PENDÊNCIA CONHECIDA: o legado encolhia o círculo e a fonte em tela estreita
 * (`MQ_COMPACT`). O novo não encolhe. Se isso incomodar em telas pequenas, o
 * lugar de resolver é o `buildAppearanceLauncher`.
 */
function setupResponsiveLayout(
  panel: HTMLDivElement,
  getPosition: () => 'left' | 'right'
): () => void {
  const mqMobile = window.matchMedia(MQ_MOBILE);
  const mqCompact = window.matchMedia(MQ_COMPACT);

  const applyLayout = () => {
    const mobile = mqMobile.matches;
    const compact = !mobile && mqCompact.matches;
    applyPanelLayout(panel, getPosition(), mobile, compact);
  };

  applyLayout();
  mqMobile.addEventListener('change', applyLayout);
  mqCompact.addEventListener('change', applyLayout);

  // Devolvido para que a chegada da branding possa reposicionar o painel: a
  // posição é lida por getter justamente porque ela muda depois do `fetch`.
  return applyLayout;
}

function buildPanel(position: 'left' | 'right', zIndex: number): HTMLDivElement {
  const panel = document.createElement('div');
  Object.assign(panel.style, {
    position: 'fixed',
    bottom: 'calc(20px + env(safe-area-inset-bottom, 0px))',
    [position]: position === 'left'
      ? 'calc(20px + env(safe-area-inset-left, 0px))'
      : 'calc(20px + env(safe-area-inset-right, 0px))',
    width: PANEL_WIDTH_DEFAULT,
    height: PANEL_HEIGHT_DEFAULT,
    maxHeight: PANEL_MAX_HEIGHT,
    borderRadius: '24px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.12)',
    background: '#ffffff',
    overflow: 'hidden',
    display: 'none',
    zIndex: String(zIndex),
  } satisfies Partial<CSSStyleDeclaration>);

  return panel;
}

function buildIframe(
  appUrl: string,
  bootstrap: {
    widgetKey: string;
    apiUrl: string;
    primaryColor: string;
    parentOrigin: string;
  }
): HTMLIFrameElement {
  const iframe = document.createElement('iframe');
  const url = new URL(appUrl);
  url.searchParams.set('widgetKey', bootstrap.widgetKey);
  url.searchParams.set('apiUrl', bootstrap.apiUrl);
  url.searchParams.set('primaryColor', bootstrap.primaryColor);
  url.searchParams.set('parentOrigin', bootstrap.parentOrigin);
  iframe.src = url.toString();
  iframe.title = 'Chat de atendimento';
  iframe.setAttribute(
    'allow',
    'clipboard-write; camera *; autoplay; fullscreen; geolocation'
  );
  iframe.setAttribute('referrerpolicy', 'origin');
  Object.assign(iframe.style, {
    width: '100%',
    height: '100%',
    border: 'none',
    display: 'block',
    background: '#F8FAFC',
  } satisfies Partial<CSSStyleDeclaration>);
  return iframe;
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', main, { once: true });
} else {
  main();
}
